import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import { userFacingError } from "../authorization";
import { runExpiry } from "../executionRetention";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";
import { stringRecord } from "./utils";
import { clientGeneratableIdSchema } from "../id";

/**
 * Validate that `commit` is still the branch HEAD on the expected workflow and
 * the workflow isn't paused, then create the run + pending execution. Shared by
 * the `runStep` and `runStepTests` mutators so the
 * authorization and row-creation semantics can't drift between run kinds.
 */
export async function insertStepRun(
  tx: Transaction,
  {
    runId,
    workflow,
    commit,
    ephemeral,
    subject,
    tenantId,
  }: {
    runId: string;
    executionId: string;
    stepDir: string;
    workflow: {
      id: string;
      pausedAt: number | null;
      retentionSeconds: number;
    };
    commit: { id: string; branchId: string };
    purpose: string;
    ephemeral: boolean;
    subject: string;
    tenantId: string;
  }
) {
  // `scope.commit` already proved write access via the branch.
  // Re-fetch the branch row only to validate that `commit` is still HEAD
  // and that it lives on the workflow the caller asked to run.
  const [branch] = await tx.run(
    zql.workflowBranches.where("id", commit.branchId)
  );
  if (!branch?.headCommitId) throw userFacingError("Branch has no head commit");
  if (branch.workflowId !== workflow.id)
    throw userFacingError("Commit not found");
  if (branch.headCommitId !== commit.id)
    throw userFacingError(`Cannot run ${subject} on a non-HEAD commit`);
  if (branch.name === "main" && workflow.pausedAt != null)
    throw userFacingError("Workflow is paused");

  const createdAt = Date.now();
  await tx.mutate.workflowRuns.insert({
    id: runId,
    workflowId: workflow.id,
    commitId: commit.id,
    branchId: commit.branchId,
    createdAt,
    expiresAt: runExpiry(createdAt, workflow.retentionSeconds),
    ephemeral,
    interactive: true,
    runStatus: "running",
    hasFailed: false,
    tenantId,
  });
}

export interface MockRequestOptions {
  method?: string;
  queryParams?: Record<string, string>;
  body?: string;
}

export function generateMockHttpRequest(
  publicRoute: string | null,
  options: MockRequestOptions = {}
): string {
  const { method = "GET", queryParams, body } = options;
  let path = publicRoute ?? "/";

  if (queryParams && Object.keys(queryParams).length > 0) {
    const params = new URLSearchParams(queryParams).toString();
    path = `${path}?${params}`;
  }

  const hasBody = body !== undefined && body.length > 0;
  const contentType = hasBody ? "application/json" : null;

  const httpMessage = `${method} ${path} HTTP/1.1\r
accept: text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8\r
accept-encoding: gzip, deflate\r
accept-language: en-US,en;q=0.9\r
connection: keep-alive\r
${contentType ? `content-type: ${contentType}\r\n` : ""}${hasBody ? `content-length: ${body.length}\r\n` : ""}host: localhost\r
sec-fetch-dest: document\r
sec-fetch-mode: navigate\r
sec-fetch-site: none\r
sec-fetch-user: ?1\r
user-agent: Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36\r
x-forwarded-for: 127.0.0.1\r
\r
${body ?? ""}`;
  return httpMessage;
}

export const runStepSchema = z.object({
  runId: clientGeneratableIdSchema,
  workflowId: z.string(),
  commitId: z.string(),
  stepDir: z.string(),
  executionId: clientGeneratableIdSchema,
  ephemeral: z.boolean().optional(),
  input: z.string().optional(),
  mockRequest: z
    .object({
      method: z.string().optional(),
      queryParams: stringRecord.optional(),
      body: z.string().optional(),
    })
    .optional(),
});

export const runStep = mutator({
  schema: runStepSchema,
  resolve: {
    workflow: { kind: "workflow", from: "workflowId" },
    commit: { kind: "commit", from: "commitId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.workflow.spaceId,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: { runId, stepDir, executionId, ephemeral, workflow, commit },
  }) => {
    await insertStepRun(tx, {
      runId,
      executionId,
      stepDir,
      workflow: {
        ...workflow,
        retentionSeconds: workflow.retentionSeconds!,
      },
      commit,
      purpose: "step",
      ephemeral: ephemeral ?? false,
      subject: "a step",
      tenantId: ctx.tenantId,
    });
    const [branch] = await tx.run(
      zql.workflowBranches.where("id", commit.branchId)
    );
    if (!branch) return;
    // lastRunAt only feeds recency sorting, so skipping the write when it's
    // already fresh avoids hot-row contention and Zero replication fan-out.
    const now = Date.now();
    if (branch.lastRunAt === null || branch.lastRunAt < now - 45_000) {
      await tx.mutate.workflowBranches.update({
        id: branch.id,
        lastRunAt: now,
      });
    }
  },
});
